import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { OKTA_AUTH } from '@okta/okta-angular';
import { catchError, from, lastValueFrom, map, of, retry, defer } from 'rxjs';

import { ILogger } from '@shure/shared/angular/utils/logging';

// Global variable to store an in-flight (inProgress) session check promise
// this helps to prevent concurrent calls to session.exists()
let inFlightSessionCheck: Promise<boolean> | null = null;
let logger: ILogger | undefined = undefined;

/**
 * Guard to check if the user's Okta session is still available.
 * Intermittent network issues can cause false negatives. Therefore,
 * the guard has a retry mechanism built in.
 *
 * Tests have shown that if there is a false negative (due to a network glitch),
 * the majority of the time, the first retry succeeds. If the user's session
 * is truly invalid, then once all retries have been exhausted, the tokens will
 * be cleared and the promise resolves to False.
 *
 * @param _route - The activated route snapshot
 * @param _state - The router state snapshot
 * @returns {Observable<boolean>}
 */
export const oktaSessionGuard: CanActivateFn = (_route, _state): Promise<boolean> => {
	// If there's already a session check in progress, return that promise
	if (inFlightSessionCheck) {
		return inFlightSessionCheck;
	}

	// Create and cache a new session check promise
	// We are not "awaiting" here so that the same promise is returned to concurrent callers.
	// No catch here because performSessionCheck handles all errors internally, and always resolves,
	// and because the underlying call to okta never throws.
	inFlightSessionCheck = performSessionCheck();

	// Clear the cached promise when the promise resolves, with 500ms delay
	inFlightSessionCheck.finally(() => {
		setTimeout(() => {
			inFlightSessionCheck = null;
		}, 500);
	});

	return inFlightSessionCheck;
};

function performSessionCheck(): Promise<boolean> {
	const oktaAuth = inject(OKTA_AUTH);
	logger = inject(ILogger, { optional: true })?.createScopedLogger('oktaSessionGuard');

	return lastValueFrom(
		defer(() => from(oktaAuth.session.exists())).pipe(
			map((result) => {
				if (result) return true;
				logger?.error('performSessionCheck', 'session.exists() failed. Throwing exception to trigger retry');
				throw new Error('session.exists() failed.');
			}),
			retry({
				count: 3,
				delay: 250
			}),
			catchError(() => {
				// if an error occurs here, we've exhausted all retries, and still have no session.
				oktaAuth.tokenManager.clear();
				return of(false);
			})
		)
	);
}
